import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL;
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN;
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI;

const TOKEN_KEY = 'admin_id_token';
const ACCESS_TOKEN_KEY = 'admin_access_token';
const REFRESH_TOKEN_KEY = 'admin_refresh_token';
const CODE_VERIFIER_KEY = 'admin_pkce_code_verifier';
const OAUTH_STATE_KEY = 'admin_oauth_state';

function randomString(length = 64) {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const values = new Uint8Array(length);
    window.crypto.getRandomValues(values);
    return Array.from(values, (v) => charset[v % charset.length]).join('');
}

function base64UrlEncode(bytes) {
    let binary = '';
    bytes.forEach((b) => {
        binary += String.fromCharCode(b);
    });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256Base64Url(value) {
    const data = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
}

function getQueryParam(name) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
}

function clearAuthStorage() {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(CODE_VERIFIER_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
}

function getIdToken() {
    return sessionStorage.getItem(TOKEN_KEY);
}

async function exchangeCodeForTokens(code) {
    const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
    if (!codeVerifier) {
        throw new Error('Missing PKCE code verifier in session storage.');
    }

    const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: COGNITO_CLIENT_ID,
        code,
        redirect_uri: REDIRECT_URI,
        code_verifier: codeVerifier,
    });

    const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
    });

    const payload = await response.json();
    if (!response.ok) {
        throw new Error(payload.error_description || payload.error || 'Failed to exchange auth code for tokens.');
    }

    sessionStorage.setItem(TOKEN_KEY, payload.id_token || '');
    sessionStorage.setItem(ACCESS_TOKEN_KEY, payload.access_token || '');
    sessionStorage.setItem(REFRESH_TOKEN_KEY, payload.refresh_token || '');

    sessionStorage.removeItem(CODE_VERIFIER_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);

    // Clean ?code=...&state=... from the URL
    window.history.replaceState({}, document.title, window.location.pathname);

    return payload;
}

async function startLogin() {
    const state = randomString(32);
    const codeVerifier = randomString(96);
    const codeChallenge = await sha256Base64Url(codeVerifier);

    sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
    sessionStorage.setItem(OAUTH_STATE_KEY, state);

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: COGNITO_CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        scope: 'openid email profile',
        state,
        code_challenge_method: 'S256',
        code_challenge: codeChallenge,
    });

    window.location.assign(`${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`);
}

function logout() {
    clearAuthStorage();

    const params = new URLSearchParams({
        client_id: COGNITO_CLIENT_ID,
        logout_uri: REDIRECT_URI,
    });

    window.location.assign(`${COGNITO_DOMAIN}/logout?${params.toString()}`);
}

async function apiFetch(path, options = {}) {
    const token = getIdToken();
    if (!token) {
        throw new Error('Missing ID token.');
    }

    const headers = {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
    };

    const response = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers,
    });

    if (response.status === 401 || response.status === 403) {
        throw new Error('Unauthorized');
    }

    return response;
}

function App() {
    const [themes, setThemes] = useState([]);
    const [prompts, setPrompts] = useState([]);
    const [newTheme, setNewTheme] = useState({
        theme_name: '',
        subreddits: '',
        search_queries: '',
    });

    const [isAuthenticated, setIsAuthenticated] = useState(Boolean(getIdToken()));
    const [isLoading, setIsLoading] = useState(true);
    const [authError, setAuthError] = useState('');
    const [dataError, setDataError] = useState('');

    const hasConfig = useMemo(() => {
        return Boolean(API_BASE && COGNITO_DOMAIN && COGNITO_CLIENT_ID && REDIRECT_URI);
    }, []);

    const fetchData = useCallback(async () => {
        setDataError('');

        const [tRes, pRes] = await Promise.all([
            apiFetch('/themes'),
            apiFetch('/prompts'),
        ]);

        const themesData = await tRes.json();
        const promptsData = await pRes.json();

        setThemes(themesData.items || []);
        setPrompts(promptsData.items || []);
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function boot() {
            if (!hasConfig) {
                setAuthError('Missing required React env configuration for Cognito/API.');
                setIsLoading(false);
                return;
            }

            try {
                const error = getQueryParam('error');
                const errorDescription = getQueryParam('error_description');
                if (error) {
                    throw new Error(errorDescription || error);
                }

                const code = getQueryParam('code');
                const returnedState = getQueryParam('state');
                const savedState = sessionStorage.getItem(OAUTH_STATE_KEY);

                if (code) {
                    if (!returnedState || !savedState || returnedState !== savedState) {
                        throw new Error('OAuth state validation failed.');
                    }
                    await exchangeCodeForTokens(code);
                }

                const token = getIdToken();
                if (!token) {
                    if (!cancelled) {
                        setIsAuthenticated(false);
                        setIsLoading(false);
                    }
                    return;
                }

                await fetchData();

                if (!cancelled) {
                    setIsAuthenticated(true);
                    setIsLoading(false);
                }
            } catch (err) {
                clearAuthStorage();
                if (!cancelled) {
                    setIsAuthenticated(false);
                    setAuthError(err.message || 'Authentication failed.');
                    setIsLoading(false);
                }
            }
        }

        boot();

        return () => {
            cancelled = true;
        };
    }, [fetchData, hasConfig]);

    const addTheme = async (e) => {
        e.preventDefault();
        setDataError('');

        try {
            const response = await apiFetch('/themes', {
                method: 'POST',
                body: JSON.stringify({
                    ...newTheme,
                    subreddits: newTheme.subreddits.split(',').map((s) => s.trim()).filter(Boolean),
                    search_queries: newTheme.search_queries.split(',').map((s) => s.trim()).filter(Boolean),
                    is_active: true,
                }),
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.error || 'Failed to create theme.');
            }

            setNewTheme({ theme_name: '', subreddits: '', search_queries: '' });
            await fetchData();
        } catch (err) {
            if (err.message === 'Unauthorized') {
                logout();
                return;
            }
            setDataError(err.message || 'Failed to create theme.');
        }
    };

    const deleteTheme = async (name) => {
        setDataError('');

        try {
            const response = await apiFetch(`/themes/${encodeURIComponent(name)}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.error || 'Failed to delete theme.');
            }

            await fetchData();
        } catch (err) {
            if (err.message === 'Unauthorized') {
                logout();
                return;
            }
            setDataError(err.message || 'Failed to delete theme.');
        }
    };

    const togglePrompt = async (id) => {
        setDataError('');

        try {
            const response = await apiFetch(`/prompts/${encodeURIComponent(id)}/activate`, {
                method: 'POST',
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.error || 'Failed to activate prompt.');
            }

            await fetchData();
        } catch (err) {
            if (err.message === 'Unauthorized') {
                logout();
                return;
            }
            setDataError(err.message || 'Failed to activate prompt.');
        }
    };

    if (isLoading) {
        return (
            <div style={{ padding: '40px', maxWidth: '900px', margin: 'auto', fontFamily: 'system-ui' }}>
                <h1>Reddit Lead Admin</h1>
                <p>Loading…</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div style={{ padding: '40px', maxWidth: '900px', margin: 'auto', fontFamily: 'system-ui' }}>
                <h1>Reddit Lead Admin</h1>
                <p>You need to sign in to continue.</p>
                {authError ? <p style={{ color: 'red' }}>{authError}</p> : null}
                <button
                    onClick={() => startLogin().catch((err) => setAuthError(err.message || 'Failed to start login.'))}
                    style={{ padding: '10px 14px', backgroundColor: '#007bff', color: '#fff', border: 'none', cursor: 'pointer' }}
                >
                    Sign in
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: '40px', maxWidth: '900px', margin: 'auto', fontFamily: 'system-ui' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 style={{ margin: 0 }}>Reddit Lead Admin</h1>
                <button
                    onClick={logout}
                    style={{ padding: '10px 14px', backgroundColor: '#333', color: '#fff', border: 'none', cursor: 'pointer' }}
                >
                    Sign out
                </button>
            </div>

            {dataError ? <p style={{ color: 'red' }}>{dataError}</p> : null}

            <section style={{ marginBottom: '40px', border: '1px solid #ddd', padding: '20px' }}>
                <h2>Add New Theme</h2>
                <form onSubmit={addTheme} style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                    <input
                        placeholder="Theme Name"
                        value={newTheme.theme_name}
                        onChange={(e) => setNewTheme({ ...newTheme, theme_name: e.target.value })}
                        required
                    />
                    <input
                        placeholder="Subreddits (e.g. saas, startup)"
                        value={newTheme.subreddits}
                        onChange={(e) => setNewTheme({ ...newTheme, subreddits: e.target.value })}
                        required
                    />
                    <input
                        placeholder="Keywords (e.g. looking for, help with)"
                        value={newTheme.search_queries}
                        onChange={(e) => setNewTheme({ ...newTheme, search_queries: e.target.value })}
                        required
                    />
                    <button
                        type="submit"
                        style={{ padding: '10px', backgroundColor: '#007bff', color: '#fff', border: 'none' }}
                    >
                        Save Theme
                    </button>
                </form>
            </section>

            <h2>Active Themes</h2>
            <table width="100%" border="1" cellPadding="10" style={{ borderCollapse: 'collapse' }}>
                <thead>
                <tr style={{ backgroundColor: '#f4f4f4' }}>
                    <th>Theme</th>
                    <th>Subreddits</th>
                    <th>Keywords</th>
                    <th>Action</th>
                </tr>
                </thead>
                <tbody>
                {Array.isArray(themes) && themes.map((t) => (
                    <tr key={t.theme_name}>
                        <td>{t.theme_name}</td>
                        <td>{t.subreddits?.join(', ')}</td>
                        <td>{t.search_queries?.join(', ')}</td>
                        <td>
                            <button onClick={() => deleteTheme(t.theme_name)} style={{ color: 'red' }}>
                                Delete
                            </button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            <h2 style={{ marginTop: '40px' }}>System Prompts</h2>
            <ul>
                {Array.isArray(prompts) && prompts.map((p) => (
                    <li key={p.prompt_id} style={{ marginBottom: '10px' }}>
                        <strong>{p.prompt_id}</strong>{' '}
                        {p.is_active ? '✅ (Active)' : <button onClick={() => togglePrompt(p.prompt_id)}>Activate</button>}
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default App;